import React, { useState, useEffect } from 'react'

interface FreshnessIndicatorProps {
  updatedAt: string | null
  onRefresh?: () => void
  showRefreshButton?: boolean
  isRefreshing?: boolean
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Unknown'

  const now = Date.now()
  const updated = new Date(dateStr).getTime()
  const diffMs = now - updated

  // Handle future timestamps (clock skew)
  if (diffMs < 0) return 'Just now'

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (seconds < 10) return 'Just now'
  if (seconds < 60) return `${seconds}s ago`
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function getFreshnessColor(dateStr: string | null): string {
  if (!dateStr) return 'var(--muted)'

  const now = Date.now()
  const updated = new Date(dateStr).getTime()
  const ageSeconds = Math.floor((now - updated) / 1000)

  // Green: <1 min, Yellow: 1-5 min, Red: >5 min
  if (ageSeconds < 60) return 'var(--green)'
  if (ageSeconds < 300) return 'var(--yellow)'
  return 'var(--red)'
}

export function FreshnessIndicator({
  updatedAt,
  onRefresh,
  showRefreshButton = false,
  isRefreshing = false,
}: FreshnessIndicatorProps) {
  const [, forceUpdate] = useState(0)

  // Update display every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => forceUpdate((n) => n + 1), 10000)
    return () => clearInterval(interval)
  }, [])

  const color = getFreshnessColor(updatedAt)
  const timeText = formatRelativeTime(updatedAt)

  return (
    <span
      className="freshness-indicator"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color,
      }}
    >
      <span>{timeText}</span>
      {showRefreshButton && onRefresh && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRefresh()
          }}
          disabled={isRefreshing}
          className="refresh-btn"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: isRefreshing ? 'default' : 'pointer',
            color: 'var(--muted)',
            fontSize: 12,
            opacity: isRefreshing ? 0.5 : 1,
            transition: 'opacity 0.2s',
          }}
          title="Refresh odds"
        >
          <span
            style={{
              display: 'inline-block',
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
            }}
          >
            ↻
          </span>
        </button>
      )}
    </span>
  )
}
